import { StripeUtils } from "@deathstar/reuse";
import { PaymentIntentCreateParams, } from "@deathstar/types/northstar";
import { PaymentIntentMetadata } from "@deathstar/types/stripe";
import { BillingInformation, PayBillFormData } from "@deathstar/types/waypoint";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CircularProgress, MenuItem, Select, TextField, Tooltip } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import Stripe from "stripe";
import { Checkbox } from "../checkbox/checkbox";
import { ConfirmationDialog } from "../confirmationDialog/confirmationDialog";
import { useSnackbar } from "../hooks/useSnackbar";
import { InputField } from "../inputField/inputField";
import { PrimaryButton } from "../primaryButton/primaryButton";

interface IItem {
    amount: number | null;
    description: string | null;
    policyNumber: string;
}

export interface IOptions {
    billingInfo: BillingInformation;
    isLoadingBillingInfo: boolean;
    createPayment(options: PaymentIntentCreateParams | PayBillFormData): Promise<Pick<Stripe.PaymentIntent, "id">>;
    defaultValues?: {
        description?: string;
        invoiceNumber?: string;
        items?: IItem[];
        paymentMethodId?: string | null;
    };
    metadata?: Partial<Record<PaymentIntentMetadata, string>>;
    onError?: () => void;
    onSuccess?: () => void;
}

export function PaymentForm({
    billingInfo,
    isLoadingBillingInfo,
    createPayment: _createPayment,
    defaultValues,
    metadata,
    onError,
    onSuccess,
}: IOptions) {
    const form = useForm<Omit<PaymentIntentCreateParams, "items"> & { items: IItem[] }>({
        defaultValues: {
            description: defaultValues?.description || "Insurance invoice",
            method: "charge_automatically",
            paymentMethod: defaultValues?.paymentMethodId || null,
            dueDate: moment().format("YYYY-MM-DD"),
            doNotEmail: false,
            items: defaultValues?.items || [{ amount: null, description: "Insurance premium", policyNumber: "" }],
            customInvoiceNumber: defaultValues?.invoiceNumber || "",
        },
    });
    const itemFields = useFieldArray({
        control: form.control,
        name: "items",
        rules: { required: { value: true, message: "At least one item is required" } },
    });

    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const createPayment = useMutation({
        mutationFn: async (options: PaymentIntentCreateParams | PayBillFormData) => {
            return _createPayment(options);
        },
    });

    const defaultPaymentMethodId = billingInfo?.paymentMethods.find((pm) => pm.isDefault)?.id;
    const customerEmail = billingInfo?.email;
    const { method, doNotEmail } = useWatch({ control: form.control });
    useEffect(() => {
        if (defaultPaymentMethodId && !defaultValues?.paymentMethodId) {
            form.setValue("paymentMethod", defaultPaymentMethodId as string);
        }
    }, [defaultPaymentMethodId, defaultValues?.paymentMethodId, form]);

    useEffect(() => {
        if (customerEmail === null) {
            form.setValue("doNotEmail", true);
        }
    }, [customerEmail, form]);

    const paymentMethodId = useWatch({ control: form.control, name: "paymentMethod" });
    const paymentMethod = billingInfo?.paymentMethods.find((pm) => pm.id === paymentMethodId);
    const totalAmount = useWatch({ control: form.control, name: "items" }).reduce((total, item) => total + (item.amount || 0), 0);
    const feeAmount = paymentMethod
        ? StripeUtils.fees.shouldIncludeFeeInTotal(paymentMethod.type as "card" | "us_bank_account") && totalAmount < 100_000
            ? StripeUtils.fees.getFeeAmount(
                  StripeUtils.formatting.dollarsToCents(totalAmount),
                  paymentMethod.type as "card" | "us_bank_account"
              )
            : 0
        : 0;
    const totalString = (totalAmount + StripeUtils.formatting.centsToDollars(feeAmount)).toLocaleString("en-us", {
        style: "currency",
        currency: "USD",
    });

    return (
        <>
            <form
                className="w-full max-w-none lg:max-w-lg"
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <div className="w-full space-y-4 px-8 pt-4">
                    <InputField
                        label="Transaction description"
                        classes={{ label: "text-gray-500", inputContainer: "py-1" }}
                        placeholder="Example description"
                        {...form.register("description")}
                    />
                    <InputField
                        label="Agency invoice number"
                        classes={{ label: "text-gray-500", inputContainer: "py-1" }}
                        placeholder="(optional)"
                        {...form.register("customInvoiceNumber")}
                    />
                    <div className="space-y-2">
                        <p className="">Transaction items</p>
                        <div className="ml-2 space-y-2">
                            {itemFields.fields.map((field, index) => (
                                <div key={field.id} className="grid grid-cols-[1rem,7rem,1fr,8rem] gap-2">
                                    <button
                                        className="text-gray-500 opacity-100 transition-opacity peer-hover:opacity-0"
                                        onClick={() => {
                                            itemFields.remove(index);
                                        }}
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                    <InputField
                                        classes={{ label: "text-gray-500", inputContainer: "py-1", root: "peer" }}
                                        placeholder="100,000"
                                        startAdornment="$"
                                        {...form.register(`items.${index}.amount`, {
                                            required: { message: "Amount is required", value: true },
                                            min: { value: 0, message: "Amount cannot be negative" },
                                            valueAsNumber: true,
                                        })}
                                        type="number"
                                        step={0.01}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                    <Controller
                                        control={form.control}
                                        name={`items.${index}.description`}
                                        render={({ field }) => (
                                            <Autocomplete
                                                {...field}
                                                freeSolo
                                                options={[
                                                    "Insurance premium",
                                                    "Down payment",
                                                    "Escrow deposit",
                                                    "Agency Processing Expense",
                                                ]}
                                                renderInput={(params) => (
                                                    <TextField
                                                        variant="outlined"
                                                        {...params}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            classes: {
                                                                root: "text-sm bg-gray-100 w-full rounded outline-none ring-navigator-blue p-0 shadow-inner",
                                                                focused: "ring-2",
                                                                input: "outline-none px-3 py-0 h-[28px] ring-0 !bg-transparent",
                                                                notchedOutline: "border-0",
                                                            },
                                                        }}
                                                        placeholder="Description"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                )}
                                                autoSelect
                                                onChange={(_, value) => {
                                                    form.setValue(`items.${index}.description`, value);
                                                }}
                                            />
                                        )}
                                    />
                                    <InputField
                                        classes={{ label: "text-gray-500", inputContainer: "py-1", root: "peer" }}
                                        placeholder="Policy #"
                                        {...form.register(`items.${index}.policyNumber`, {
                                            required: { message: "Policy number is required", value: true },
                                        })}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="flex items-start justify-between">
                            <button
                                type="button"
                                className="ml-4 rounded px-4 text-sm font-medium text-navigator-primary-light ring-navigator-primary-light hover:text-navigator-primary-dark focus:ring-2"
                                onClick={() => {
                                    if (itemFields.fields.length === 0) {
                                        itemFields.append({ amount: null, description: "Insurance premium", policyNumber: "" });
                                    } else if (itemFields.fields.length === 1) {
                                        itemFields.append({ amount: null, description: "Agency Processing Expense", policyNumber: "" });
                                    } else {
                                        itemFields.append({ amount: null, description: "", policyNumber: "" });
                                    }
                                }}
                            >
                                Add item
                            </button>
                            <div className="text-right tabular-nums">
                                <p className="ml-8">{totalString}</p>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div>
                            <div className="flex items-baseline gap-4">
                                <input
                                    id="charge_automatically"
                                    type="radio"
                                    value="charge_automatically"
                                    {...form.register("method")}
                                    className="peer enabled:cursor-pointer"
                                />
                                <label
                                    htmlFor="charge_automatically"
                                    className="flex flex-col peer-enabled:cursor-pointer peer-disabled:opacity-60"
                                >
                                    <span>Autocharge client</span>
                                    <span className="text-sm text-gray-500">Automatically charge a payment method on file</span>
                                </label>
                            </div>

                            {method === "charge_automatically" ? (
                                <div className="ml-7 mt-4 space-y-4">
                                    {billingInfo?.paymentMethods.length ? (
                                        <Controller
                                            control={form.control}
                                            name="paymentMethod"
                                            rules={{ required: { value: true, message: "Payment method is required" } }}
                                            render={({ field }) => (
                                                <Select {...field} variant="outlined" classes={{ outlined: "py-1 shadow text-sm" }}>
                                                    {billingInfo.paymentMethods.map((pm) => (
                                                        <MenuItem key={pm.id} value={pm.id}>
                                                            <div className="flex items-center gap-2 text-sm">
                                                                {pm.isDefault && <CheckCircleIcon className="h-4 w-4 text-blue-600" />}
                                                                {pm.description}
                                                            </div>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    ) : (
                                        <div>
                                            <p className="text-sm text-gray-500">No payment methods on file</p>
                                        </div>
                                    )}
                                    {billingInfo?.automaticBillingEnabled ? (
                                        <p className="text-gray-500">Note: The client has authorized automatic payments.</p>
                                    ) : (
                                        <p className="text-yellow-700">
                                            Note: The client has not authorized automatic payments. You may still proceed if necessary.
                                        </p>
                                    )}
                                </div>
                            ) : null}
                        </div>

                        <hr />

                        <div>
                            <Tooltip title="This feature is not yet available" placement="left-start">
                                <div className="flex items-baseline gap-4 opacity-50">
                                    <input
                                        id="send_invoice"
                                        type="radio"
                                        value="send_invoice"
                                        {...form.register("method")}
                                        className="peer enabled:cursor-pointer"
                                        disabled
                                    />
                                    <label htmlFor="send_invoice" className="flex flex-col peer-enabled:cursor-pointer">
                                        <span>Request payment</span>
                                        <span className="text-sm text-gray-500">Send the client a request for payment</span>
                                    </label>
                                </div>
                            </Tooltip>

                            {method === "send_invoice" ? (
                                <div className="ml-7 mt-4 space-y-4">
                                    <InputField
                                        startAdornment={<span className="whitespace-nowrap text-gray-400">Due on</span>}
                                        {...form.register("dueDate", {
                                            required: { value: true, message: "Due date is required" },
                                            validate: (value) =>
                                                (moment(value).isValid() && moment(value).isSameOrAfter(moment(), "day")) ||
                                                "Invalid due date",
                                        })}
                                        type="date"
                                        classes={{ inputContainer: "py-1", root: "w-52" }}
                                    />
                                    <div className="space-y-1">
                                        <Checkbox
                                            classes={{ input: "w-4 h-4", root: customerEmail === null ? "opacity-60" : "" }}
                                            label="Include surcharge (for credit card payments)"
                                            {...form.register("includeCreditCardFee")}
                                        />
                                        <Checkbox
                                            classes={{ input: "w-4 h-4", root: customerEmail === null ? "opacity-60" : "" }}
                                            label="Do not email"
                                            {...form.register("doNotEmail")}
                                            disabled={customerEmail === null}
                                        />
                                        {doNotEmail && (
                                            <p className="text-sm text-gray-500">
                                                A link to the invoice will need to be provided to the client manually.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
                <hr className="my-4" />
                <div className="flex justify-between px-8 pb-4">
                    <div>
                        {billingInfo ? (
                            customerEmail === null &&
                            method === "send_invoice" && (
                                <p className="text-sm text-red-600">No email address is set on the client's billing profile.</p>
                            )
                        ) : isLoadingBillingInfo ? null : (
                            <p className="text-sm text-red-600">No billing profile found</p>
                        )}
                        {createPayment.isError && <p className="text-sm text-red-600">{createPayment.error.message}</p>}
                    </div>
                    <PrimaryButton
                        className="whitespace-nowrap"
                        disabled={(!customerEmail && method === "send_invoice") || createPayment.isPending || saving}
                        onClick={() => {
                            form.handleSubmit(
                                () => {
                                    setConfirming(true);
                                },
                                (errors) => {
                                    const errorsArray = Object.entries(errors)
                                        .map(([, value]) => value.message)
                                        .filter(Boolean);
                                    if (errorsArray.length) {
                                        useSnackbar.add(errorsArray.join("\n"), { variant: "error" });
                                    }
                                }
                            )();
                        }}
                    >
                        {createPayment.isPending || saving ? (
                            <>
                                <CircularProgress size="1rem" classes={{ root: "text-white" }} />
                                <span>Processing</span>
                            </>
                        ) : method === "charge_automatically" ? (
                            "Process payment"
                        ) : (
                            "Create payment request"
                        )}
                    </PrimaryButton>
                </div>
            </form>
            <ConfirmationDialog open={confirming} onClose={() => setConfirming(false)}>
                <ConfirmationDialog.TitleGroup>
                    <ConfirmationDialog.Title>{method === "send_invoice" ? "Send invoice" : "Charge client"}</ConfirmationDialog.Title>
                    <ConfirmationDialog.CloseIconButton />
                </ConfirmationDialog.TitleGroup>
                <ConfirmationDialog.Description>{`Are you sure you want to ${
                    method === "send_invoice"
                        ? `${doNotEmail ? "create" : "send"} this invoice for ${totalString}`
                        : `debit the selected payment method for ${totalString}`
                }?${method === "charge_automatically" || !doNotEmail ? " This cannot be undone." : ""}`}</ConfirmationDialog.Description>
                <ConfirmationDialog.ActionGroup>
                    <ConfirmationDialog.CancelButton />
                    <ConfirmationDialog.PrimaryButton
                        disabled={saving}
                        onClick={async () => {
                            setConfirming(false);
                            setSaving(true);
                            const data = form.getValues();
                            createPayment.mutate(
                                {
                                    ...data,
                                    items: data.items.map((item) => ({
                                        amount: item.amount || 0,
                                        description: item.description + " - " + item.policyNumber,
                                    })),
                                    dueDate: moment(data.dueDate).endOf("day").toISOString(),
                                    metadata,
                                },
                                {
                                    async onSuccess() {
                                        form.reset();
                                        await onSuccess?.();
                                        setSaving(false);
                                    },
                                    async onError() {
                                        await onError?.();
                                        setSaving(false);
                                    },
                                }
                            );
                        }}
                    />
                </ConfirmationDialog.ActionGroup>
            </ConfirmationDialog>
        </>
    );
}
